<script>
import { mapActions, mapGetters } from 'vuex'
import CandidateSortOrder from '@/shared/constants/candidate/CandidateSortOrder'
import { MaxPreferredQueueSize } from '@/shared/constants/booking/BookingSelectEducators'
import { BookableEducatorQueryRequestDTO } from '@/models/candidate/BookableEducatorQueryRequestDTO'
import BookableEducatorList from './bookable-educator-list.vue'
import PreferredEducatorQueue from './preferred-educator-queue.vue'

const { NameAsc } = CandidateSortOrder

export default {
  name: 'BookingSelectEducatorsStep',
  components: {
    BookableEducatorList,
    PreferredEducatorQueue,
  },
  props: {
    schedules: {
      type: Array,
      required: true,
    },
    selectedCertificationIds: {
      type: Array,
      default: () => [],
    },
    locationId: {
      type: Number,
      required: true,
    },
    classificationId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      educators: [],
      totalCount: 0,
      currentPage: 1,
      totalPages: 1,
      isLoading: false,
      hasError: false,
      searchTerm: '',
      sortOrder: NameAsc,
      preferredQueue: [],
      isPreferredQueueDragging: false,
    }
  },
  computed: {
    ...mapGetters({
      isSendToANZUK: 'bookings/isSendToANZUK',
    }),
    addedEducatorIds() {
      return this.preferredQueue.map(({ id }) => id)
    },
    isQueueFull() {
      return this.preferredQueue.length >= MaxPreferredQueueSize
    },
    totalScheduleDays() {
      return this.schedules.length
    },
    requestPayload() {
      return new BookableEducatorQueryRequestDTO({
        schedules: this.schedules,
        certificationIds: this.selectedCertificationIds,
        classificationId: this.classificationId,
        locationId: this.locationId,
        sortOrder: this.sortOrder,
        searchTerm: this.searchTerm,
        pageNumber: this.currentPage,
      })
    },
  },
  watch: {
    preferredQueue(queue) {
      this.$emit(
        'educators-updated',
        queue.map(({ id }) => id)
      )
    },
  },
  created() {
    this.fetchEducators()
  },
  methods: {
    ...mapActions({
      queryBookableEducators: 'candidate/queryBookableEducators',
    }),
    async fetchEducators() {
      this.isLoading = true
      this.hasError = false

      const { data, error } = await this.queryBookableEducators(
        this.requestPayload
      )

      this.isLoading = false

      if (error) {
        this.hasError = true
        this.educators = []
        return
      }

      this.educators = data?.candidates ?? []
      this.totalCount = data?.totalCount ?? 0
      this.currentPage = data?.currentPage ?? 1
      this.totalPages = data?.totalPages ?? 1
    },
    onSearch(term) {
      this.searchTerm = term
      this.currentPage = 1

      this.fetchEducators()
    },
    onPageChange(page) {
      this.currentPage = page

      this.fetchEducators()
    },
    onAddEducator(educator) {
      if (this.isQueueFull) return

      if (this.addedEducatorIds.includes(educator.id)) return

      this.preferredQueue = [...this.preferredQueue, educator]

      if (this.isQueueFull) {
        this.$toast.warning(
          this.$t('bookingCreateV2.selectEducatorsStep.queueFullWarningToast')
        )
      }
    },
    onQueueChange(updated) {
      this.preferredQueue = updated
    },
    onEducatorDragRemove(educator) {
      this.preferredQueue = this.preferredQueue.filter(
        (e) => e.id !== educator.id
      )
    },
    onPreferredQueueDragStateChange(isDragging) {
      this.isPreferredQueueDragging = isDragging
    },
  },
}
</script>

<template>
  <v-row no-gutters class="align-stretch booking-select-educators-step">
    <v-col cols="12" md="7" class="pr-8">
      <BookableEducatorList
        class="fill-height"
        :educators="educators"
        :is-loading="isLoading"
        :has-error="hasError"
        :total-pages="totalPages"
        :current-page="currentPage"
        :added-educator-ids="addedEducatorIds"
        :is-queue-full="isQueueFull"
        :total-schedule-days="totalScheduleDays"
        :show-remove-overlay="isPreferredQueueDragging"
        @search="onSearch"
        @page-change="onPageChange"
        @retry="fetchEducators"
        @add="onAddEducator"
        @drag-remove="onEducatorDragRemove"
      />
    </v-col>

    <v-col cols="12" md="5">
      <PreferredEducatorQueue
        class="fill-height"
        :class="{ 'opacity-50 pointer-events-none': isSendToANZUK }"
        :educators="preferredQueue"
        @change="onQueueChange"
        @drag-state-change="onPreferredQueueDragStateChange"
      />
    </v-col>
  </v-row>
</template>

<style lang="scss">
$bookable-educator-list-height: 650px;

.booking-select-educators-step {
  min-height: $bookable-educator-list-height;
}

.bookable-list {
  height: $bookable-educator-list-height;
}

.preferred-queue {
  height: $bookable-educator-list-height + 130px;
}
</style>
